'use client';

import { useState } from 'react';
import { Calculator, User, Activity } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';

function IdealWeightCalculatorClient() {
    const [gender, setGender] = useState<'male' | 'female'>('male');
    const [height, setHeight] = useState('');
    const [age, setAge] = useState('');
    const [idealWeight, setIdealWeight] = useState<{
        devine: number;
        robinson: number;
        miller: number;
        hamwi: number;
        healthy: { min: number; max: number };
    } | null>(null);

    const calculateIdealWeight = () => {
        if (!height) return;

        const heightCm = parseFloat(height);
        const heightInches = heightCm / 2.54;
        const heightFeet = Math.floor(heightInches / 12);
        const heightInchesRemainder = heightInches % 12;

        // Devine Formula (1974)
        const devine = gender === 'male'
            ? 50 + 2.3 * (heightInches - 60)
            : 45.5 + 2.3 * (heightInches - 60);

        // Robinson Formula (1983)
        const robinson = gender === 'male'
            ? 52 + 1.9 * (heightInches - 60)
            : 49 + 1.7 * (heightInches - 60);

        // Miller Formula (1983)
        const miller = gender === 'male'
            ? 56.2 + 1.41 * (heightInches - 60)
            : 53.1 + 1.36 * (heightInches - 60);

        // Hamwi Formula (1964)
        const hamwi = gender === 'male'
            ? 48 + 2.7 * (heightInches - 60)
            : 45.5 + 2.2 * (heightInches - 60);

        // Healthy BMI Range (18.5 to 24.9)
        const healthyMin = 18.5 * Math.pow(heightCm / 100, 2);
        const healthyMax = 24.9 * Math.pow(heightCm / 100, 2);

        setIdealWeight({
            devine: Math.round(devine * 10) / 10,
            robinson: Math.round(robinson * 10) / 10,
            miller: Math.round(miller * 10) / 10,
            hamwi: Math.round(hamwi * 10) / 10,
            healthy: {
                min: Math.round(healthyMin * 10) / 10,
                max: Math.round(healthyMax * 10) / 10
            }
        });
    };

    const resetCalculator = () => {
        setGender('male');
        setHeight('');
        setAge('');
        setIdealWeight(null);
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <Calculator className="h-8 w-8 text-green-600" />
                </div>
                <ToolPageIntro title="Ideal Weight Calculator">
                Calculate your ideal weight using multiple proven formulas including Devine, Robinson, Miller, and Hamwi methods for comprehensive weight assessment.
            </ToolPageIntro>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                        <div className="flex gap-4">
                            <button
                                onClick={() => setGender('male')}
                                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${gender === 'male'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                Male
                            </button>
                            <button
                                onClick={() => setGender('female')}
                                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${gender === 'female'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                Female
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Height (cm)</label>
                        <input
                            type="number"
                            value={height}
                            onChange={(e) => setHeight(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            placeholder="Enter your height"
                            min="100"
                            max="250"
                            step="0.1"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Age (optional)</label>
                        <input
                            type="number"
                            value={age}
                            onChange={(e) => setAge(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            placeholder="Enter your age"
                            min="1"
                            max="120"
                        />
                    </div>
                </div>

                <div className="flex gap-4">
                    <button
                        onClick={calculateIdealWeight}
                        disabled={!height}
                        className={`flex-1 py-3 px-6 rounded-lg font-medium transition-colors ${!height
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                            }`}
                    >
                        Calculate Ideal Weight
                    </button>
                    <button
                        onClick={resetCalculator}
                        className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        Reset
                    </button>
                </div>
            </div>

            {idealWeight && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Ideal Weight Results</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="text-sm text-gray-600 mb-1">Devine Formula (1974)</div>
                            <div className="text-2xl font-bold text-blue-600">{idealWeight.devine} kg</div>
                        </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                            <div className="text-sm text-gray-600 mb-1">Robinson Formula (1983)</div>
                            <div className="text-2xl font-bold text-green-600">{idealWeight.robinson} kg</div>
                        </div>

                        <div className="bg-purple-50 p-4 rounded-lg">
                            <div className="text-sm text-gray-600 mb-1">Miller Formula (1983)</div>
                            <div className="text-2xl font-bold text-purple-600">{idealWeight.miller} kg</div>
                        </div>

                        <div className="bg-orange-50 p-4 rounded-lg">
                            <div className="text-sm text-gray-600 mb-1">Hamwi Formula (1964)</div>
                            <div className="text-2xl font-bold text-orange-600">{idealWeight.hamwi} kg</div>
                        </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-2">Healthy Weight Range (BMI 18.5-24.9)</h3>
                        <div className="text-2xl font-bold text-gray-700">
                            {idealWeight.healthy.min} - {idealWeight.healthy.max} kg
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                            This range is based on the healthy BMI range and is considered the optimal weight for your height.
                        </p>
                    </div>

                    <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-2">About These Formulas</h3>
                        <div className="text-sm text-gray-600 space-y-2">
                            <p><strong>Devine Formula (1974):</strong> Widely used in medical settings for calculating ideal body weight.</p>
                            <p><strong>Robinson Formula (1983):</strong> A modification of the Devine formula, considered more accurate.</p>
                            <p><strong>Miller Formula (1983):</strong> Developed based on data from a large population study.</p>
                            <p><strong>Hamwi Formula (1964):</strong> One of the original formulas, still used by some healthcare professionals.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default IdealWeightCalculatorClient;
